import { useState, useEffect } from 'react';
import { Plus, Search, Eye, Edit, Trash2, FileText, Loader2, Package, CheckCircle, XCircle } from 'lucide-react';
import { propostaService, Proposta } from '../../../services/propostaService';
import { PropostaForm } from './PropostaForm';
import { PropostaPreview } from './PropostaPreview';
import { ProdutoServicoManagement } from './ProdutoServicoManagement';

const statusColors: Record<string, string> = {
  rascunho: 'bg-gray-100 text-gray-700',
  enviada: 'bg-blue-100 text-blue-700',
  aprovada: 'bg-green-100 text-green-700',
  rejeitada: 'bg-red-100 text-red-700',
};

const statusLabels: Record<string, string> = {
  rascunho: 'Rascunho',
  enviada: 'Enviada',
  aprovada: 'Aprovada',
  rejeitada: 'Rejeitada',
};

interface PropostaManagementProps {
  onNavigateToClients?: () => void;
}

export function PropostaManagement({ onNavigateToClients }: PropostaManagementProps = {}) {
  const [propostas, setPropostas] = useState<Proposta[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [view, setView] = useState<'list' | 'form' | 'preview' | 'produtos'>('list');
  const [selectedProposta, setSelectedProposta] = useState<Proposta | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    if (view === 'list') loadPropostas();
  }, [view, statusFilter, currentPage]);

  const loadPropostas = async () => {
    setIsLoading(true);
    try {
      const result = await propostaService.list({ status: statusFilter || undefined, page: currentPage, limit: 15 });
      setPropostas(result.data);
      setTotalPages(result.pagination.totalPages);
    } catch (error) {
      console.error('Erro ao carregar propostas:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Tem certeza que deseja excluir esta proposta?')) return;
    try {
      await propostaService.delete(id);
      loadPropostas();
    } catch (error) {
      console.error('Erro ao excluir:', error);
    }
  };

  const handleUpdateStatus = async (id: string, status: string) => {
    const action = status === 'aprovada' ? 'aprovar' : 'rejeitar';
    if (!confirm(`Tem certeza que deseja ${action} esta proposta?`)) return;
    try {
      await propostaService.updateStatus(id, status);
      loadPropostas();
    } catch (error) {
      console.error('Erro ao atualizar status:', error);
    }
  };

  const handleEdit = async (proposta: Proposta) => {
    const full = await propostaService.getById(proposta.id);
    setSelectedProposta(full);
    setView('form');
  };

  const handlePreview = async (proposta: Proposta) => {
    const full = await propostaService.getById(proposta.id);
    setSelectedProposta(full);
    setView('preview');
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  const filteredPropostas = propostas.filter(p => 
    p.client.razaoSocial.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.numero.toString().includes(searchTerm)
  );

  if (view === 'form') {
    return (
      <PropostaForm
        proposta={selectedProposta}
        onBack={() => { setView('list'); setSelectedProposta(null); }}
        onSaved={() => { setView('list'); setSelectedProposta(null); loadPropostas(); }}
        onAddClient={onNavigateToClients}
      />
    );
  }

  if (view === 'preview' && selectedProposta) {
    return (
      <PropostaPreview
        proposta={selectedProposta}
        onBack={() => { setView('list'); setSelectedProposta(null); }}
      />
    );
  }

  if (view === 'produtos') {
    return <ProdutoServicoManagement onBack={() => setView('list')} />;
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 justify-between">
        <div className="flex gap-2">
          <button onClick={() => { setSelectedProposta(null); setView('form'); }}
            className="flex items-center gap-2 px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700">
            <Plus className="w-4 h-4" /> Nova Proposta
          </button>
          <button onClick={() => setView('produtos')}
            className="flex items-center gap-2 px-4 py-2 bg-slate-600 text-white rounded-lg hover:bg-slate-700">
            <Package className="w-4 h-4" /> Produtos/Serviços
          </button>
        </div>
        <div className="flex gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input type="text" placeholder="Buscar..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg w-64 text-gray-900 bg-white" />
          </div>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-gray-900 bg-white">
            <option value="">Todos os status</option>
            <option value="rascunho">Rascunho</option>
            <option value="enviada">Enviada</option>
            <option value="aprovada">Aprovada</option>
            <option value="rejeitada">Rejeitada</option>
          </select>
        </div>
      </div>


      {/* Table */}
      <div className="bg-white border rounded-lg overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-cyan-600 animate-spin" />
          </div>
        ) : filteredPropostas.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">Nenhuma proposta encontrada</p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nº</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cliente</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tipo</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Valor</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Data</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filteredPropostas.map(proposta => (
                <tr key={proposta.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium text-gray-900">#{proposta.numero}</td>
                  <td className="px-4 py-3 text-gray-900">{proposta.client.nomeFantasia || proposta.client.razaoSocial}</td>
                  <td className="px-4 py-3 text-gray-700">{proposta.tipo === 'vendas' ? 'Produtos' : 'Serviços'}</td>
                  <td className="px-4 py-3 font-medium text-gray-900">{formatCurrency(Number(proposta.valorTotal))}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[proposta.status]}`}>
                      {statusLabels[proposta.status]}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {new Date(proposta.dataEmissao).toLocaleDateString('pt-BR')}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => handlePreview(proposta)} className="p-2 hover:bg-gray-100 rounded" title="Visualizar">
                        <Eye className="w-4 h-4 text-gray-600" />
                      </button>
                      <button onClick={() => handleEdit(proposta)} className="p-2 hover:bg-gray-100 rounded" title="Editar">
                        <Edit className="w-4 h-4 text-blue-600" />
                      </button>
                      {(proposta.status === 'enviada' || proposta.status === 'rascunho') && (
                        <>
                          <button onClick={() => handleUpdateStatus(proposta.id, 'aprovada')} className="p-2 hover:bg-green-100 rounded" title="Aprovar">
                            <CheckCircle className="w-4 h-4 text-green-600" />
                          </button>
                          <button onClick={() => handleUpdateStatus(proposta.id, 'rejeitada')} className="p-2 hover:bg-red-100 rounded" title="Rejeitar">
                            <XCircle className="w-4 h-4 text-red-600" />
                          </button>
                        </>
                      )}
                      <button onClick={() => handleDelete(proposta.id)} className="p-2 hover:bg-gray-100 rounded" title="Excluir">
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 p-4 border-t">
            <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}
              className="px-3 py-1 border rounded hover:bg-gray-50 disabled:opacity-50">Anterior</button>
            <span className="text-sm text-gray-600">Página {currentPage} de {totalPages}</span>
            <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}
              className="px-3 py-1 border rounded hover:bg-gray-50 disabled:opacity-50">Próxima</button>
          </div>
        )}
      </div>
    </div>
  );
}
